import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { StaffRepository } from '../api/staff-repository'
import { staffKeys } from './use-staff'
import { logger } from '@/core/logger'

/**
 * Stamp the caller's own `staff/{uid}.welcomeDialogSeenAt`. Mirrors the
 * fire-and-forget shape of `useMarkAnalyticsViewed`, with one extra
 * concern: invalidate the `staffKeys.byUid` query so the dialog
 * un-mounts on the same tick the write lands (no flash on a remount).
 *
 * Both arguments are required for the write but the hook accepts nulls
 * up-front to keep call sites simple; a no-op fires when either is
 * missing rather than throwing at the boundary.
 */
export function useMarkWelcomeDialogSeen(
  clubId: string | null,
  uid: string | null,
): () => void {
  const qc = useQueryClient()
  const repo = useMemo(
    () => (clubId ? new StaffRepository(clubId) : null),
    [clubId],
  )
  return useCallback(() => {
    if (!repo || !clubId || !uid) return
    repo
      .markWelcomeDialogSeen(uid)
      .then(() => {
        void qc.invalidateQueries({ queryKey: staffKeys.byUid(clubId, uid) })
      })
      .catch((e) => logger.w('markWelcomeDialogSeen failed', e))
  }, [repo, clubId, uid, qc])
}
